// src/js/utils/rosterSort.ts
// Phase 253 (253-05) — dependency-free sort-state machine for the `/mc/<key>` Player Roster
// table's click-to-sort headers (D-07).
//
// This module exists because `src/js/mc/roster.ts` touches the DOM (querySelector, appendChild,
// aria-sort/textContent writes), so `node tests/mc/*.test.ts` could never load it — the same
// reason `src/js/utils/feedTail.ts` (Phase 243) and `src/js/utils/chatThread.ts` (Phase 251)
// were split out of their own page-touching modules before it. Every decision worth pinning
// (the toggle cycle, the null-parsing rule, the null-last comparator and the aria/glyph mapping)
// lives here instead. Every export below is pure: no `import` statement of any kind, no DOM
// global, no network call, no timer.
//
// This is also the first hand-rolled sortable table in this codebase — every other wide MC table
// (`all-stats`, leaderboard) is either DataTables.net or server-sorted only, with no client
// re-sort. `.planning/phases/253-server-detail-today-stats-player-roster/253-UI-SPEC.md` §4's
// click-to-sort header contract is the authoritative spec this module implements.

// ---------------------------------------------------------------------------------------
// Exported constants
// ---------------------------------------------------------------------------------------

/** Shown on every inactive sortable header at all times (UI-SPEC §4) so the click-to-sort
 *  affordance is discoverable without hovering — never hidden until the user finds it. */
export const SORT_GLYPH_NONE = '⇅'; // ⇅

/** Active-ascending glyph (UI-SPEC §4). */
export const SORT_GLYPH_ASC = '▲'; // ▲

/** Active-descending glyph (UI-SPEC §4). */
export const SORT_GLYPH_DESC = '▼'; // ▼

/**
 * FOUR MIRRORED SITES, not three — this docblock used to name only three sites and call the
 * contract by a name implying a set of three; that wording was wrong (D-14) and is corrected
 * here. These two constants must always agree BY VALUE with:
 *   1. `App\Models\McAnalyticsModel::ROSTER_DEFAULT_SORT_KEY` — the single named constant
 *      `getPlayerRoster()`'s own sort helper (`sortRosterRowsByActivePlaytimeDescNullsLast()`)
 *      reads, rather than a restated literal.
 *   2. `app/Presenters/templates/Mc/detail.latte`'s active `<th>` — the ONE header carrying BOTH
 *      `aria-sort="descending"` and `class="mc-roster-th--active"` (Active Playtime, since plan
 *      273-10; it used to be Time Played before D-14's flip).
 *   3. `src/js/mc/roster.ts`'s `ROSTER_COLUMNS` — the ONE entry carrying `active: true`.
 *   4. These two constants, right here.
 * Changing any one of the four without the other three makes the table disagree with its own
 * label on first paint — `initRoster()` in `roster.ts` reads the DOM's own marked header on init
 * rather than trusting these blindly, but they are still the fallback when no header is marked,
 * so they must stay correct. `tools/check-mc-roster-sort-consistency-static.mjs` now enforces
 * this agreement MECHANICALLY (parses all four sites by value, fails the build on disagreement) —
 * so the "still the fallback" caveat above is no longer the ONLY protection, but it is still true
 * and still worth stating: a header-reading bug in `initRoster()` would not be caught by that
 * static gate, only by these constants staying honest.
 *
 * Active introduces far more nulls than Playtime ever had (most players have no observed
 * `active_playtime` at all pre-rollout — see 273-CONTEXT.md's live-evidence table), so
 * `compareSortValues()`'s NULLS-ALWAYS-LAST discipline below is now load-bearing on the roster's
 * DEFAULT view, not merely an edge case a user could hit by choosing an unusual sort column.
 */
export const DEFAULT_SORT_KEY = 'active-playtime';
export const DEFAULT_SORT_DIRECTION: SortDirection = 'desc';

// ---------------------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------------------

export type SortDirection = 'asc' | 'desc';

export type SortKind = 'text' | 'numeric';

export interface RosterSortState {
    key: string;
    direction: SortDirection;
}

export type SortValue = number | string | null;

// ---------------------------------------------------------------------------------------
// Exported functions
// ---------------------------------------------------------------------------------------

/**
 * UI-SPEC §4's toggle cycle. When `clickedKey` differs from `current.key`, the new state opens
 * with `'desc'` for a numeric column (biggest-first is the useful opener for a stat column) or
 * `'asc'` for a text column (A-Z is the useful opener for a name column). When `clickedKey` IS
 * already the active key, flip the direction. Single-column sort only — there is no multi-key
 * state, so a previously-active column simply stops being active; the caller does not need to
 * clear anything separately.
 */
export function nextSortState(
    current: RosterSortState,
    clickedKey: string,
    clickedKind: SortKind,
): RosterSortState {
    if (clickedKey !== current.key) {
        return { key: clickedKey, direction: clickedKind === 'numeric' ? 'desc' : 'asc' };
    }
    return { key: clickedKey, direction: current.direction === 'desc' ? 'asc' : 'desc' };
}

/**
 * Parses one `data-sort-*` attribute's raw string form into a comparable value. An empty string
 * or `null` — the wire form the template emits for a null metric (`detail.latte`:
 * `$row['playtime'] === null ? '' : $row['playtime']`) — always returns `null`, the "not
 * measured" sentinel `compareSortValues()` sorts last. For `'numeric'`, `Number.parseFloat` the
 * string and return `null` when the result is `NaN` (defensive: the template never emits a
 * non-numeric numeric-kind value today, but a parse failure must still degrade to "unmeasured"
 * rather than sorting a `NaN` as if it were a number). For `'text'`, return the trimmed,
 * lower-cased string so the sort is case-insensitive. A literal `'0'` MUST return the number `0`,
 * never `null` — a measured zero is a real value, not an absence (the same zero-vs-null
 * discipline `tests/Mc/AllStatsNullLastSortTest.php` already pins server-side).
 */
export function parseSortValue(raw: string | null, kind: SortKind): SortValue {
    if (raw === null || raw === '') {
        return null;
    }
    if (kind === 'numeric') {
        const parsed = Number.parseFloat(raw);
        return Number.isNaN(parsed) ? null : parsed;
    }
    return raw.trim().toLowerCase();
}

/**
 * NULLS ALWAYS LAST, in BOTH directions. This check runs BEFORE the direction is applied — that
 * ordering is exactly what stops a null from behaving like a zero at the top of a descending
 * column, matching the convention `tests/Mc/AllStatsNullLastSortTest.php` already pins
 * server-side for the `/mc/all-stats` table (D-12 there, the same rule this phase's D-07 must
 * not silently re-decide). When only `a` is null, `a` sorts after `b` (`1`); when only `b` is
 * null, `b` sorts after `a` (`-1`); when both are null, they are equal (`0`). Only once neither
 * value is null does the real comparison run: `localeCompare` for two strings, subtraction for
 * two numbers, negated for `'desc'`.
 */
export function compareSortValues(a: SortValue, b: SortValue, direction: SortDirection): number {
    if (a === null && b === null) return 0;
    if (a === null) return 1;
    if (b === null) return -1;

    let cmp: number;
    if (typeof a === 'string' && typeof b === 'string') {
        cmp = a.localeCompare(b);
    } else {
        cmp = (a as number) - (b as number);
    }
    return direction === 'desc' ? -cmp : cmp;
}

/**
 * Returns a permutation of `0..values.length-1`, sorted by `compareSortValues()` with the
 * ORIGINAL INDEX as the tie-break so the result is deterministic and stable regardless of the
 * host engine's own sort-stability guarantee. Returning indices rather than reordering `values`
 * itself (or rows) is what keeps this module free of any DOM type — the caller in `roster.ts`
 * maps these indices back onto its own `<tr>` array.
 */
export function sortIndices(values: SortValue[], direction: SortDirection): number[] {
    const indices = values.map((_, i) => i);
    indices.sort((ia, ib) => {
        const cmp = compareSortValues(values[ia], values[ib], direction);
        return cmp !== 0 ? cmp : ia - ib;
    });
    return indices;
}

/**
 * Maps the active column to `'ascending'` / `'descending'` and every other column to `'none'` —
 * the exact three-value `aria-sort` vocabulary UI-SPEC §6 requires kept in sync with the actual
 * on-screen order on every toggle.
 */
export function ariaSortValue(isActive: boolean, direction: SortDirection): 'ascending' | 'descending' | 'none' {
    if (!isActive) return 'none';
    return direction === 'asc' ? 'ascending' : 'descending';
}

/**
 * Returns `SORT_GLYPH_ASC` for the active-ascending header, `SORT_GLYPH_DESC` for the
 * active-descending header, and `SORT_GLYPH_NONE` for every inactive header — UI-SPEC §4's
 * three-state icon contract.
 */
export function sortGlyph(isActive: boolean, direction: SortDirection): string {
    if (!isActive) return SORT_GLYPH_NONE;
    return direction === 'asc' ? SORT_GLYPH_ASC : SORT_GLYPH_DESC;
}
